import {
  PickEventDetail,
  Raycaster,
  Vector3,
  Web3DEventName,
  Web3DViewer,
} from '@technology/web3d';
import { IconsPlugin, PointIcon } from '@technology/web3d-plugin-icons';
import { TileMapModel } from '@technology/web3d-plugin-tilemap';
import { SingleCapabilityProviderBase } from './../base/single-provider-base';
import { TileMapCapabilityProvider } from './tile-map-provider';
import { QuantizedMeshModel } from '@trimble/web3d-plugin-quantized-mesh';

const raycastHeight = 10000;

export class MapIconsCapabilityProvider extends SingleCapabilityProviderBase<TileMapModel> {
  public idKey = 'modelId' as const;

  private get iconsPlugin(): IconsPlugin {
    const plugin = this.viewer.plugins.get(IconsPlugin);
    if (!plugin) {
      throw new Error('IconsPlugin is not available on the viewer');
    }
    return plugin;
  }

  constructor(
    viewer: Web3DViewer,
    private readonly tileMapProvider: TileMapCapabilityProvider,
    /*
     * ModelId that should be considered for ray casting when determining height of icons.
     */
    private readonly terrainModelId?: string,
    public onIconClick?: (arg: PickEventDetail) => void | Promise<void>
  ) {
    super(viewer);
    this.iconsPlugin.settings.clusterRadius = 10;
    this.viewer.addEventListener(
      'pick' as Web3DEventName.Pick,
      this.handleClick.bind(this)
    );
  }

  private handleClick(e: Event): void {
    const event = e as CustomEvent<PickEventDetail>;
    if (event.detail.modelId === 'icons') {
      void this.onIconClick?.(event.detail);
    }
  }

  public override async cleanup(): Promise<void> {
    this.viewer.removeEventListener(
      'pick' as Web3DEventName.Pick,
      this.handleClick.bind(this)
    );
  }

  private readonly raycaster = new Raycaster();
  private static readonly raycastDirection = new Vector3(0, 0, -1);

  private raycastHeight(origin: Vector3): number | null {
    const terrainModel = this.viewer
      .getModels()
      .find((m) => m.modelId === this.terrainModelId) as
      | QuantizedMeshModel
      | undefined;

    if (!terrainModel) {
      return this.#backgroundElevation;
    }

    this.raycaster.set(
      new Vector3(origin.x, origin.y, raycastHeight),
      MapIconsCapabilityProvider.raycastDirection
    );
    const height = terrainModel.raycastHeight(this.raycaster);

    return height;
  }

  public async addIcons(icons: PointIcon[]): Promise<void> {
    const pointIcons = await Promise.all(
      icons.map(async (icon) => {
        const pos = await this.tileMapProvider.coordinateToPosition([
          icon.position.x,
          icon.position.y,
        ]);
        const height = this.raycastHeight(new Vector3(pos.x, pos.y, 0));
        return {
          id: icon.id,
          iconPath: icon.iconPath,
          position: new Vector3(pos.x, pos.y, height ?? 0),
          size: icon.size,
          iconVerticalAlignment: icon.iconVerticalAlignment,
          clusterGroup: icon.clusterGroup ?? '',
        };
      })
    );
    await this.iconsPlugin.add(pointIcons);
  }

  public async removeIcons(
    filterFn: (icon: PointIcon) => boolean
  ): Promise<void> {
    const icons = this.iconsPlugin.getIcons().filter(filterFn);
    if (icons.length > 0) {
      // Remove all icons in a single batch operation to avoid parallel atlas texture updates
      await this.iconsPlugin.remove(icons);
    }
  }

  /*
   * Snaps all icons to the terrain model by raycasting down from a high altitude.
   * If the icon is more than 5 units above or below the terrain, it is repositioned.
   */
  public async snapToTerrain(): Promise<void> {
    const icons = this.iconsPlugin.getIcons();
    const threshold = 5;
    for (const icon of icons) {
      const height = this.raycastHeight(icon.position);
      if (height !== null && Math.abs(icon.position.z - height) > threshold) {
        await this.iconsPlugin.remove(icon);
        icon.position.z = height;
        await this.iconsPlugin.add(icon);
      }
    }
  }

  public async updateIcon(
    id: number,
    iconPath?: string,
    position?: [number, number]
  ): Promise<void> {
    const icon = this.iconsPlugin.getIcons().find((icon) => icon.id === id);
    if (icon) {
      await this.iconsPlugin.remove(icon);
      if (iconPath) {
        icon.iconPath = iconPath;
      }
      if (position) {
        const pos = await this.tileMapProvider.coordinateToPosition(position);
        const height = this.raycastHeight(new Vector3(pos.x, pos.y, 0));
        icon.position = new Vector3(pos.x, pos.y, height ?? icon.position.z);
      }
      await this.iconsPlugin.add(icon);
    }
  }

  public getIcon(id: number): PointIcon | undefined {
    return (
      this.iconsPlugin.getIcons().find((icon) => icon.id === id) ?? undefined
    );
  }

  #backgroundElevation = 0;

  public set backgroundElevation(elevation: number) {
    if (this.#backgroundElevation !== elevation) {
      this.#backgroundElevation = elevation;
      this.iconsPlugin.getIcons().map((icon) => {
        icon.position.z = this.#backgroundElevation;
      });
      // Requires a model update for effect to be visible
      // (this can be a camera movement or a call to model.update())
    }
  }
}
